import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { CxbDatePipe } from '../../pipes/cxb-date.pipe';
import * as moment from 'moment-timezone';
import { AlwaysError } from '../../../core/services/always-error.service';

@Component({
  selector: 'osn-cxb-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
  ],
})
export class DateTimeInputComponent implements ControlValueAccessor, OnInit, Validator {
  @Input() public hintLabel: string;
  @Input() public label: string;
  @Input() public errorLabel: string;
  //eslint-disable-next-line
  @Input('data-testid') public testId: string;
  @Input() public maxOffset?: string; // Days
  @Input() public min?: string;
  @Input() public readonly = false;

  // public min = this.formatDate(new Date());
  // public max?: string;
  private maxDate: moment.Moment;
  private minDate: moment.Moment;
  public value: string;
  public isDisabled: boolean;
  private readonly dateFormat = 'DD/MM/YYYY, hh:mm A';
  private isInvalid = false;

  public config = {
    format: this.dateFormat,
    showGoToCurrent: false,
    firstDayOfWeek: 'mo',
    timezone: 'GMT',
    min: null,
    max: null,
  };

  constructor(private readonly cdr: ChangeDetectorRef, private readonly datePipe: CxbDatePipe, public readonly alwaysError: AlwaysError) {}

  public validate(control: AbstractControl<any, any>): ValidationErrors {
    this.isInvalid = false;
    if (this.maxDate || this.minDate) {
      if (this.isDisabled) {
        return null;
      }
      const tempDate = moment.tz(this.value, 'DD/MM/YYYY, hh:mm a', true, 'GMT+0');
      if (this.value !== undefined && !tempDate.isValid()) {
        this.isInvalid = true;
        return { nan: true };
      }
      if (this.maxDate && tempDate.isAfter(this.maxDate)) {
        return { maxError: true };
      } else if (this.minDate && tempDate.isBefore(this.minDate)) {
        return { minError: true };
      }
    }
    return null;
  }
  public registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  public ngOnInit() {
    const maxOffsetNumber = +this.maxOffset;
    if (!isNaN(maxOffsetNumber)) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      this.maxDate = moment.tz('GMT+0').hours(0).minutes(0).seconds(0).milliseconds(0).add(maxOffsetNumber, 'days');
      this.config.max = this.maxDate.format('DD/MM/YYYY, hh:mm a');
    }
    if (this.min) {
      this.minDate = moment.tz(this.min, 'GMT+0');
      this.config.min = this.minDate.format('DD/MM/YYYY, hh:mm a');
    }
    /*
    } else {
      this.minDate = moment.tz('GMT+0').seconds(0).milliseconds(0);
    }
    */
    if (this.readonly) {
      this.setDisabledState(true);
    }
  }

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};
  public onValidationChange: Function = () => {};

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public setValue(value: string, emit = true): void {
    if (value === '') {
      this.value = undefined;
      this.propagateChange(undefined);
      return;
    }
    this.value = value;
    this.onValidationChange();
    if (emit) {
      if (!this.isInvalid) {
        let formattedValue = moment.tz(value, 'GMT+0').format('YYYY-MM-DDTHH:mm');
        if (isNaN(formattedValue as unknown as number)) {
          formattedValue = moment.tz(value, 'DD/MM/YYYY, hh:mm a', 'GMT+0').format('YYYY-MM-DDTHH:mm');
        }
        this.propagateChange(`${formattedValue}:00.000Z`);
      } else {
        this.propagateChange(undefined);
      }
    }
  }

  private formatDate(date: Date | string): string {
    return this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm', '+0000');
  }

  private formatDateForInput(date: Date | string): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy, hh:mm a', '+0000');
  }

  public writeValue(obj: any): void {
    if (obj instanceof Date) {
      this.setValue(this.formatDate(obj), false);
    } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(obj)) {
      // Test for YYYY-MM-DDTHH-mm ('2021-01-01T10:00') format
      this.setValue(obj, false);
    } else if (obj) {
      this.setValue(this.formatDateForInput(obj), false);
    } else {
      this.setValue(undefined, false);
    }
    this.cdr.detectChanges();
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.detectChanges();
  }
}
